import { moonbeam } from '@0xtorch/core'
import { createClient } from '../../client/createClient'
import { createSubscan } from '../../explorer'
import type { Chain } from '../types'

type CreateMoonbeamChainParameters = {
  explorerApiKey?: string
  explorerProxyUrl?: string
  rpcEndpoints?: string[]
}

export const createMoonbeamChain = ({
  explorerApiKey,
  explorerProxyUrl,
  rpcEndpoints = moonbeamWebsocketRpcUrls,
}: CreateMoonbeamChainParameters): Chain => {
  return {
    /** {@link https://polkadot.subscan.io/parachain_list?status=parachain} */
    id: 2004,
    /** {@link https://github.com/paritytech/ss58-registry/blob/main/ss58-registry.json} */
    ss58Prefix: 1284,
    network: 'moonbeam-polkadot',
    name: 'Moonbeam (Polkadot)',
    blockTimeSeconds: 6,
    nativeToken: {
      name: 'Moonbeam',
      symbol: 'GLMR',
      decimals: 18,
      currency: moonbeam,
    },
    client: createClient(rpcEndpoints),
    explorer: createSubscan({
      name: 'Subscan',
      baseUrl: 'https://moonbeam.subscan.io',
      networkName: 'moonbeam',
      apiKey: explorerApiKey,
      proxyUrl: explorerProxyUrl,
    }),
    rpcEndpoints,
    assetType: 'assets',
    /** {@link https://moonbeam.subscan.io/asset_token?page=1&order=desc&order_field=holders} */
    assetMap: new Map([
      [
        64174511183114006009298114091987195453n,
        {
          asset: {
            symbol: 'PINK',
            type: 'CryptoCurrency',
            id: 'dot-finance',
            name: 'Dot Finance',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        166446646689194205559791995948102903873n,
        {
          asset: {
            symbol: 'MANTA',
            type: 'CryptoCurrency',
            id: 'manta-network',
            name: 'Manta Network',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        42259045809535163221576417993425387648n,
        {
          asset: {
            symbol: 'DOT',
            type: 'CryptoCurrency',
            id: 'polkadot',
            name: 'Polkadot',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        311091173110107856861649819128533077277n,
        {
          asset: {
            symbol: 'USDT',
            type: 'CryptoCurrency',
            id: 'tether',
            name: 'Tether',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        165823357460190568952172802245839421906n,
        {
          asset: {
            symbol: 'BNC',
            type: 'CryptoCurrency',
            id: 'bifrost-native-coin',
            name: 'Bifrost Native Coin',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        184218609779515850660274730699350567246n,
        {
          asset: {
            symbol: 'NCTR',
            type: 'CryptoCurrency',
            id: 'apillon',
            name: 'Apillon',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        224821240862170613278369189818311486111n,
        {
          asset: {
            symbol: 'ACA',
            type: 'CryptoCurrency',
            id: 'acala',
            name: 'Acala',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        166377000701797186346254371275954761085n,
        {
          asset: {
            symbol: 'USDC',
            type: 'CryptoCurrency',
            id: 'usd-coin',
            name: 'USDC',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        224077081838586484055667086558292981199n,
        {
          asset: {
            symbol: 'ASTR',
            type: 'CryptoCurrency',
            id: 'astar',
            name: 'Astar',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        110021739665376159354538090254163045594n,
        {
          asset: {
            symbol: 'aSEED',
            type: 'CryptoCurrency',
            id: 'ausd-seed-acala',
            name: 'aUSD SEED (Acala)',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        101170542313601871197860408087030232491n,
        {
          asset: {
            symbol: 'INTR',
            type: 'CryptoCurrency',
            id: 'interlay',
            name: 'Interlay',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        91372035960551235635465443179559840483n,
        {
          asset: {
            symbol: 'CFG',
            type: 'CryptoCurrency',
            id: 'centrifuge',
            name: 'Centrifuge',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        29085784439601774464560083082574142143n,
        {
          asset: {
            symbol: 'vDOT',
            type: 'CryptoCurrency',
            id: 'voucher-dot',
            name: 'Voucher DOT',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        204507659831918931608354793288110796652n,
        {
          asset: {
            symbol: 'vGLMR',
            type: 'CryptoCurrency',
            id: 'voucher-glmr',
            name: 'Voucher GLMR',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        89994634370519791027168048838578580624n,
        {
          asset: {
            symbol: 'SUB',
            type: 'CryptoCurrency',
            id: 'subsocial',
            name: 'Subsocial',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        124463719055550872076363892993240202694n,
        {
          asset: {
            symbol: 'DED',
            type: 'CryptoCurrency',
            id: 'dot-is-ded',
            name: 'DED',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        190590555344745888270686124937537713878n,
        {
          asset: {
            symbol: 'EQ',
            type: 'CryptoCurrency',
            id: 'equilibrium-token',
            name: 'Equilibrium',
            updatedAt: 0,
          },
          decimals: 9,
        },
      ],
      [
        225719522181998468294117309041779353812n,
        {
          asset: {
            symbol: 'LDOT',
            type: 'CryptoCurrency',
            id: 'liquid-staking-dot',
            name: 'Liquid Staking DOT',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        132685552157663328694213725410064821485n,
        {
          asset: {
            symbol: 'PHA',
            type: 'CryptoCurrency',
            id: 'pha',
            name: 'PHALA',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        120637696315203257380661607956669368914n,
        {
          asset: {
            symbol: 'IBTC',
            type: 'CryptoCurrency',
            id: 'interbtc',
            name: 'interBTC',
            updatedAt: 0,
          },
          decimals: 8,
        },
      ],
      [
        45647473099451451833602657905356404688n,
        {
          asset: {
            symbol: 'PEN',
            type: 'CryptoCurrency',
            id: 'pendulum-chain',
            name: 'Pendulum',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        150874409661081770150564009349448205842n,
        {
          asset: {
            symbol: 'ZTG',
            type: 'CryptoCurrency',
            id: 'zeitgeist',
            name: 'Zeitgeist',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        309163521958167876851250718453738106865n,
        {
          asset: {
            symbol: 'xcNODL',
            type: 'CryptoCurrency',
            id: 'nodle-network',
            name: 'Nodle Network',
            updatedAt: 0,
          },
          decimals: 11,
        },
      ],
      [
        69606720909260275826784788104880799692n,
        {
          asset: {
            symbol: 'HDX',
            type: 'CryptoCurrency',
            id: 'hydradx',
            name: 'Hydration',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        238111524681612888331172110363070489924n,
        {
          asset: {
            symbol: 'NEURO',
            type: 'CryptoCurrency',
            id: 'neurowebai',
            name: 'NeuroWeb',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        114018676402354620972806895487280206446n,
        {
          asset: {
            symbol: 'vASTR',
            type: 'CryptoCurrency',
            id: 'bifrost-voucher-astr',
            name: 'Bifrost Voucher ASTR',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        289989900872525819559124583375550296953n,
        {
          asset: {
            symbol: 'vMANTA',
            type: 'CryptoCurrency',
            id: 'bifrost-voucher-manta',
            name: 'Bifrost Voucher MANTA',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        144012926827374458669278577633504620722n,
        {
          asset: {
            symbol: 'FIL',
            type: 'CryptoCurrency',
            id: 'filecoin',
            name: 'Filecoin',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        125699734534028342599692732320197985871n,
        {
          asset: {
            symbol: 'RING',
            type: 'CryptoCurrency',
            id: 'darwinia-network-native-token',
            name: 'Darwinia Network',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        178794693648360392906933130845919698647n,
        {
          asset: {
            symbol: 'ETH',
            type: 'CryptoCurrency',
            id: 'ethereum',
            name: 'Ethereum',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        90225766094594282577230355136633846906n,
        {
          asset: {
            symbol: 'PDEX',
            type: 'CryptoCurrency',
            id: 'polkadex',
            name: 'Polkadex',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        124105859028862849477017063633156007283n,
        {
          asset: {
            symbol: 'wstETH',
            type: 'CryptoCurrency',
            id: 'wrapped-steth',
            name: 'Wrapped stETH',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        283870493414747423842723289889816153538n,
        {
          asset: {
            symbol: 'UNQ',
            type: 'CryptoCurrency',
            id: 'unique-network',
            name: 'Unique Network',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        36282181791341254438422467838694599751n,
        {
          asset: {
            symbol: 'WBTC',
            type: 'CryptoCurrency',
            id: 'wrapped-bitcoin',
            name: 'Wrapped Bitcoin',
            updatedAt: 0,
          },
          decimals: 8,
        },
      ],
    ]),
  }
}

export const moonbeamWebsocketRpcUrls = [
  'wss://moonbeam.public.blastapi.io',
  'wss://moonbeam-rpc.n.dwellir.com',
  'wss://moonbeam.ibp.network',
  'wss://moonbeam.dotters.network',
  'wss://wss.api.moonbeam.network',
  'wss://moonbeam.api.onfinality.io/public-ws',
  'wss://moonbeam.public.curie.radiumblock.co/ws',
  'wss://moonbeam.unitedbloc.com',
]
